import { DataFetcher } from "@/components/display";
import { SchemaFormHook } from "@/components/form";
import { Drawer, ModalHeader, useOpenModal } from "@/components/modal";
import useHasPermisos from "@/features/auth/hooks/useHasPermisos";
import CajaAssignForm, { cajaAssignFormSchema } from "@/features/cajas/caja-actual/components/caja-assign-form";
import { useCajaContext } from "@/features/cajas/caja-actual/contexts/caja-context";
import { GetConfigurationQuery } from "@/features/configuration/api/get-configuration";
import { SetConfigurationMutation } from "@/features/configuration/api/set-configuration";
import MonetizacionTables from "@/features/configuration/components/monetizacion-tables";
import VentaConfigurationForm, { ventaConfigurationFormSchema } from "@/features/configuration/components/venta-configuration-form";
import { useGetCotizacionQuery } from "@/features/monetizacion/api/get-cotizacion";
import { useGetMonedasQuery } from "@/features/monetizacion/api/get-monedas";
import { useGetTransferenciaMetodosQuery } from "@/features/monetizacion/api/get-trasnferencia-metodos";
import { SetCotizacionMutation } from "@/features/monetizacion/api/set-cotizacion";
import { SetMonedasMutation } from "@/features/monetizacion/api/set-monedas";
import { SetTrasnferenciaMetodosMutation } from "@/features/monetizacion/api/set-transferencia-metodos";
import EditCotizacionForm, { editCotizacionFormSchema } from "@/features/monetizacion/components/edit-cotizacion-form";
import EditMonedasForm, { editMonedasFormSchema } from "@/features/monetizacion/components/edit-moneda-form";
import EditTransferenciaMetodoForm, { editTransferenciaMetodoFormSchema } from "@/features/monetizacion/components/edit-transferencia-metodos-form";
import { notify } from "@/shared/notification";
import { Permiso } from "@/shared/types/permiso";
import { Container, Divider, Stack, Tab, Tabs, Typography } from "@mui/material"
import { Children, PropsWithChildren, useState } from "react"

export default function ConfigurationPage() {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);

    const hasAssignCajaPermiso = useHasPermisos(Permiso.AsignarCajas);

    return <Container component={Stack} maxWidth="lg" flex={1}>
        <Stack maxWidth={"md"} paddingY={3} gap={3} flex={1}>
            <Stack gap={2}>
                <Typography variant="h5">Configuración</Typography>
                <Tabs variant="scrollable" value={currentTabIndex} onChange={(_, v) => setCurrentTabIndex(v)} sx={{ borderBottom: "1px solid", borderBottomColor: "divider" }}>
                    <Tab label={<Typography paddingX={1} variant="inherit">Ventas / Facturación</Typography>} onClick={() => setCurrentTabIndex(0)} />
                    <Tab label={<Typography paddingX={1} variant="inherit">Monetización</Typography>} />
                    {hasAssignCajaPermiso && <Tab label={<Typography paddingX={1} variant="inherit">Asignación de Cajas</Typography>} />}
                </Tabs>
            </Stack>
            <TabPanelManager tabIndex={currentTabIndex}>
                {/* First Tab */}
                <FirstTabPanel />

                {/* Second Tab */}
                <SecondTabPanel />

                {/* Third Tab */}
                {hasAssignCajaPermiso && <ThirdTabPanel />}
            </TabPanelManager>
        </Stack>
    </Container>
}

//#region Tab Panels
interface TabPanelManagerProps extends PropsWithChildren {
    tabIndex?: number
}

function TabPanelManager({
    tabIndex = 0,
    children
}: TabPanelManagerProps) {
    const childList = Children.toArray(children);
    return childList[tabIndex]
}

function FirstTabPanel() {
    return <GetConfigurationQuery>
        {query => <DataFetcher flex={1} query={query}>
            {configuration => <SetConfigurationMutation>
                {mutation => <SchemaFormHook
                    schema={ventaConfigurationFormSchema}
                    onSubmit={async ({ value }) => {
                        await mutation.mutateAsync(value);
                        notify("Las configuraciones se han editado correctamente.")
                    }}
                    defaultValues={{ ...configuration }}
                >
                    {form => <VentaConfigurationForm form={form} />}
                </SchemaFormHook>}
            </SetConfigurationMutation>}
        </DataFetcher>}
    </GetConfigurationQuery>
}

function SecondTabPanel() {
    const openModal = useOpenModal();

    const monedas = useGetMonedasQuery();
    const metodos = useGetTransferenciaMetodosQuery();
    const cotizacion = useGetCotizacionQuery();

    return <DataFetcher query={[monedas, metodos, cotizacion]} flex={1}>
        {([monedas, metodos, cotizacion]) => <>
            <MonetizacionTables
                monedas={monedas}
                metodos={metodos}
                cotizacion={cotizacion}

                onEditMonedasClick={() => openModal("editMonedas")}
                onEditMetodosClick={() => openModal("editMetodos")}
                onEditCotizacionClick={() => openModal("editCotizacion")}
            />

            <Drawer code="editMonedas" >
                <ModalHeader>Monedas</ModalHeader>
                <SetMonedasMutation>
                    {mutation => <SchemaFormHook
                        schema={editMonedasFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("Se han editado las monedas correctamente.");
                        }}
                        defaultValues={{
                            details: structuredClone(monedas)
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <EditMonedasForm form={form} padding={3} />}
                    </SchemaFormHook>}
                </SetMonedasMutation>
            </Drawer>

            <Drawer code="editMetodos" >
                <ModalHeader>Monedas</ModalHeader>
                <SetTrasnferenciaMetodosMutation>
                    {mutation => <SchemaFormHook
                        schema={editTransferenciaMetodoFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("Se han editado las monedas correctamente.");
                        }}
                        defaultValues={{
                            details: metodos
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <EditTransferenciaMetodoForm form={form} padding={3} />}
                    </SchemaFormHook>}
                </SetTrasnferenciaMetodosMutation>
            </Drawer>

            <Drawer code="editCotizacion">
                <ModalHeader>Cotización</ModalHeader>
                <SetCotizacionMutation>
                    {mutation => <SchemaFormHook
                        schema={editCotizacionFormSchema}
                        onSubmit={async ({ value }) => {
                            await mutation.mutateAsync(value);
                            notify("La cotización se ha modificado correctamente.");
                        }}
                        defaultValues={{
                            cotizacionData: cotizacion?.cotizacionItems.reduce((prev, ci) => {
                                prev[ci.monedaId] = ci.monedaBaseRelacion
                                return prev;
                            }, {} as Record<string, number>)
                        }}
                        submitOnce
                        closeModalOnSubmit
                    >
                        {form => <EditCotizacionForm form={form} monedas={monedas} padding={3} />}
                    </SchemaFormHook>}
                </SetCotizacionMutation>
            </Drawer>
        </>}
    </DataFetcher>
}

function ThirdTabPanel() {
    const { cajaData, assign, revoke } = useCajaContext();

    return <Stack gap={3}>
        <Stack gap={1} >
            <Typography variant="h6">Asignar Caja</Typography>
            <Divider flexItem />
            <Typography>Asignar una caja a este dispositivo.</Typography>
        </Stack>
        <SchemaFormHook
            schema={cajaAssignFormSchema}
            onSubmit={async ({ value }) => {
                if (value.cajaId) await assign(value.cajaId)
                else await revoke()
                notify("La caja se ha asignado correctamente.", { variant: "success" })
            }}
            defaultValues={{
                cajaId: cajaData?.id
            }}
        >
            {form => <CajaAssignForm form={form} />}
        </SchemaFormHook>
    </Stack>
}
//#endregion